import React, { FC, useCallback } from 'react';

import { MoreMenuButton } from '@modernloop/shared/components';
import { DIVIDER_ID } from '@modernloop/shared/constants';
import { FeatureFlagBool } from '@modernloop/shared/feature-flag';
import { CrossIcon } from '@modernloop/shared/icons';
import { Box, Divider, IconButton, MenuItem, MenuItemProps, Stack, Typography, useTheme } from '@mui/material';

import BetaTag from 'src/components/BetaTag';

import { SidePanelManager } from './SidePanelManager';

type BetaFlag = {
  flag: FeatureFlagBool;
  tooltip?: string;
};

type Props = {
  children?: React.ReactNode;
  /**
   * Allow showing the beta tag in the top right of the side panel
   */
  betaFlag?: BetaFlag;
  header?: React.ReactNode;
  moreMenuOptions?: MenuItemProps[];
  onMenuOptionSelected?: (option: MenuItemProps) => void;
};

const SidePanelContainer: FC<Props> = ({ children, betaFlag, header, moreMenuOptions, onMenuOptionSelected }) => {
  const currentTheme = useTheme();
  const hideSidePanelClose = useCallback(() => {
    SidePanelManager.closeSidePanel();
  }, []);

  return (
    <Stack sx={{ height: '100%', overflow: 'hidden' }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="flex-end"
        spacing={1}
        sx={{
          backgroundColor: (theme) => theme.palette.background.default,
          top: 0,
          position: 'sticky',
          zIndex: 1,
        }}
        borderBottom={(theme) => `1px solid ${theme.palette.divider}`}
        padding={1.5}
      >
        <Box flexGrow={1}>{header}</Box>
        {betaFlag && <BetaTag flag={betaFlag.flag} tooltip={betaFlag.tooltip} />}
        {moreMenuOptions?.length && onMenuOptionSelected && (
          <MoreMenuButton
            iconSx={{
              height: '24px',
              width: '32px',
              borderRadius: '4px',
              backgroundColor: currentTheme.palette.background.contrast,
            }}
            dataTestId="side-panel-container-more-menu-button"
            color="min-contrast-grey"
          >
            {moreMenuOptions.map((option) => {
              if (option.id === DIVIDER_ID) {
                return <Divider />;
              }
              return (
                <MenuItem key={option.id} onClick={() => onMenuOptionSelected(option)}>
                  <Typography color={option.color}>{option.value}</Typography>
                </MenuItem>
              );
            })}
          </MoreMenuButton>
        )}
        <IconButton
          data-testid="side-panel-container-close-button"
          sx={{
            height: '24px',
            width: '32px',
            borderRadius: '4px',
            backgroundColor: currentTheme.palette.background.contrast,
          }}
          onClick={hideSidePanelClose}
        >
          <CrossIcon />
        </IconButton>
      </Stack>

      <Box sx={{ display: 'flex', flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>{children}</Box>
    </Stack>
  );
};

export default SidePanelContainer;
